"use client";

import { AnimatedCursor, getCursorTarget } from "./animated-cursor";
import { springSnappy, stagger } from "./motion-tokens";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useEffect, useState, useRef } from "react";

const CHORD_SEQUENCE = [
  { nash: "1", chord: "G" },
  { nash: "4", chord: "C" },
  { nash: "5", chord: "D" },
  { nash: "6m", chord: "Em" },
];

const BUTTON_POSITIONS = [
  { label: "1", gridPos: 0 },
  { label: "4", gridPos: 3 },
  { label: "5", gridPos: 4 },
  { label: "6", gridPos: 5 },
];

export function RealtimeDemo() {
  const [enteredChords, setEnteredChords] = useState<typeof CHORD_SEQUENCE>([]);
  const [activeButton, setActiveButton] = useState<number | null>(null);
  const [cursorPos, setCursorPos] = useState({ x: 40, y: 200 });
  const [isClicking, setIsClicking] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { amount: 0.3 });

  useEffect(() => {
    if (!isInView) return;
    let mounted = true;

    const runSequence = async () => {
      setEnteredChords([]);
      await sleep(1200);

      for (let i = 0; i < CHORD_SEQUENCE.length; i++) {
        if (!mounted) return;

        const btn = BUTTON_POSITIONS[i];
        if (!btn) continue;

        const targetId = `realtime-numpad-${btn.gridPos}`;
        const pos = getCursorTarget(targetId, "realtime-editor-container");
        if (!pos) {
          await sleep(500);
          continue;
        }

        setCursorPos(pos);
        await sleep(500);
        if (!mounted) return;

        setIsClicking(true);
        setActiveButton(btn.gridPos);
        await sleep(120);
        if (!mounted) return;

        setIsClicking(false);

        const chord = CHORD_SEQUENCE[i];
        if (chord) {
          setEnteredChords((prev) => [...prev, chord]);
        }

        await sleep(200);
        setActiveButton(null);
        await sleep(600);
        if (!mounted) return;
      }

      await sleep(2500);
      if (!mounted) return;

      setEnteredChords([]);
      await sleep(1000);
      if (!mounted) return;

      void runSequence();
    };

    void runSequence();
    return () => {
      mounted = false;
    };
  }, [isInView]);

  return (
    <div ref={containerRef} className="w-full max-w-2xl mx-auto">
      <div className="flex flex-row gap-4 justify-center">
        {/* Device 1: Editor */}
        <div id="realtime-editor-container" className="w-[240px] relative">
          <PhoneFrame label="Your device">
            {/* Song header */}
            <div className="bg-theme-green flex items-center px-3 py-1.5">
              <span className="text-white font-semibold text-xs flex-1">
                1. Way Maker
              </span>
              <span className="text-white font-semibold text-[10px]">
                Key: G
              </span>
            </div>

            {/* Section */}
            <div className="bg-surface-primary px-3 py-1 border-b border-border-primary">
              <span className="text-glyphs-primary font-medium text-xs">
                Chorus
              </span>
            </div>

            {/* Progression row */}
            <div className="bg-surface-foreground px-3 py-2">
              <div className="flex gap-3 font-mono text-sm min-h-[18px]">
                <AnimatePresence mode="popLayout">
                  {enteredChords.map((chord, i) => (
                    <motion.span
                      key={`local-${chord.nash}-${i}`}
                      className="font-semibold text-glyphs-primary"
                      initial={{ opacity: 0, scale: 0.8, y: -3 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    >
                      {chord.nash}
                    </motion.span>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Progression control drawer */}
            <div className="border-t-2 border-border-primary bg-surface-secondary flex flex-col mt-auto">
              {/* Drawer handle */}
              <div className="flex justify-center py-1.5">
                <div className="w-8 h-1 rounded-full bg-border-primary" />
              </div>

              {/* Extension row */}
              <div className="px-2.5 pb-2">
                <div className="grid grid-cols-6 gap-1">
                  {["maj", "min", "7", "/", "♯", "♭"].map((ext) => (
                    <button
                      key={ext}
                      className="h-5 rounded text-[8px] font-medium border border-border-primary bg-surface-tertiary text-glyphs-primary"
                    >
                      {ext}
                    </button>
                  ))}
                </div>
              </div>

              {/* Number pad */}
              <div className="px-2.5 pb-1.5">
                <div className="grid grid-cols-3 gap-1.5">
                  {[1, 2, 3, 4, 5, 6, null, 7, null].map((num, i) => {
                    if (num === null) return <div key={`empty-${i}`} />;
                    return (
                      <motion.button
                        key={num}
                        id={`realtime-numpad-${i}`}
                        className="flex items-center justify-center h-9 rounded-lg border font-bold text-sm"
                        style={{
                          backgroundColor:
                            activeButton === i
                              ? "var(--color-theme-blue)"
                              : "var(--color-surface-tertiary)",
                          borderColor: "var(--color-border-primary)",
                          color:
                            activeButton === i
                              ? "white"
                              : "var(--color-glyphs-primary)",
                        }}
                        animate={{ scale: activeButton === i ? 0.92 : 1 }}
                        transition={springSnappy}
                      >
                        {num}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </div>
          </PhoneFrame>

          <AnimatedCursor
            x={cursorPos.x}
            y={cursorPos.y}
            visible={isInView}
            clicking={isClicking}
            color="#007AFF"
          />
        </div>

        {/* Device 2: Viewer */}
        <div className="w-[240px]">
          <PhoneFrame label="Bandmate's device">
            {/* Song header (same song) */}
            <div className="bg-theme-green flex items-center px-3 py-1.5">
              <span className="text-white font-semibold text-xs flex-1">
                1. Way Maker
              </span>
              <span className="text-white font-semibold text-[10px]">
                Key: G
              </span>
            </div>

            {/* Section */}
            <div className="bg-surface-primary px-3 py-1 border-b border-border-primary">
              <span className="text-glyphs-primary font-medium text-xs">
                Chorus
              </span>
            </div>

            {/* Chord progression - updates in realtime */}
            <div className="bg-surface-foreground px-3 py-2 flex-1">
              <div className="flex gap-3 font-mono text-sm min-h-[18px]">
                <AnimatePresence mode="popLayout">
                  {enteredChords.map((chord, i) => (
                    <motion.span
                      key={`remote-${chord.nash}-${i}`}
                      className="font-semibold text-glyphs-primary rounded px-0.5"
                      initial={{
                        opacity: 0,
                        scale: 0.8,
                        backgroundColor: "rgba(65, 165, 124, 0.3)",
                      }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        backgroundColor: "rgba(65, 165, 124, 0)",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 30,
                        delay: i * stagger.fast + 0.1,
                        backgroundColor: { duration: 1.2, delay: 0.3 },
                      }}
                    >
                      {chord.nash}
                    </motion.span>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Status bar at bottom */}
            <div className="px-3 py-2.5 border-t border-border-primary bg-surface-primary mt-auto">
              <div className="flex items-center gap-1.5">
                <motion.div
                  className="w-1 h-1 rounded-full bg-theme-green"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span className="text-[10px] text-glyphs-secondary">
                  Updates appear instantly
                </span>
              </div>
            </div>
          </PhoneFrame>
        </div>
      </div>
    </div>
  );
}

function PhoneFrame({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex flex-col h-full max-w-[220px]">
      <span className="text-[10px] font-medium text-glyphs-secondary mb-1.5 px-1">
        {label}
      </span>
      <div className="rounded-[20px] border-2 border-border-primary bg-surface-secondary overflow-hidden shadow-xl flex-1 flex flex-col min-h-[360px]">
        {/* iPhone notch */}
        <div className="flex justify-center py-2 bg-surface-primary">
          <div className="w-20 h-4 rounded-full bg-surface-tertiary" />
        </div>
        <div className="flex-1 flex flex-col overflow-hidden bg-surface-foreground">
          {children}
        </div>
        {/* Home indicator */}
        <div className="flex justify-center py-1.5 bg-surface-primary">
          <div className="w-24 h-1 rounded-full bg-border-primary" />
        </div>
      </div>
    </div>
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
